/**
 * Runtime configuration for the GPS Clinics portal.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C0.2 (portal skeleton).
 *
 * WHY: the portal talks to the GPS Clinics gateway (BFF) for every API call
 * (`${GATEWAY_URL}/v1/*`). The base URL must be environment-specific
 * (dev/stage/prod), so it is injected at build time via Vite's
 * `import.meta.env.VITE_GATEWAY_URL`. `import.meta.env` is only defined under
 * Vite/Vitest; we read it defensively so unit tests (which may run a bare
 * module) fall back to a deterministic default. `||` (not `??`) so the
 * Dockerfile's empty-string default also falls back — same-origin `/api` is
 * the standing default because the stage/prod LB routes `/api` to the gateway.
 */

/**
 * The gateway base URL the portal calls. Override with `VITE_GATEWAY_URL` at
 * build time; unset/empty (stub mode) means same-origin `/api`.
 */
export const GATEWAY_URL: string =
  (import.meta.env?.VITE_GATEWAY_URL as string | undefined) || '/api';

/**
 * Whether the portal should render the holding "coming soon" page INSTEAD of the
 * app. Enabled by `VITE_COMING_SOON=true` at build time — a per-environment
 * GitHub Actions variable, exactly like `VITE_STRIPE_PUBLISHABLE_KEY`. Used on
 * PROD during the launch bake so `app.clinics` takes no real orders while the
 * backend is verified, then the variable is removed (or set false) to go live.
 * Only the exact string `'true'` enables it; unset/empty/anything else renders
 * the real app — a deliberate fail-safe so a config slip never hides the working
 * app on dev/stage/local. Read defensively (`import.meta.env?`) so a bare-module
 * unit test falls back to the app, matching `GATEWAY_URL` above.
 */
export function comingSoonEnabled(): boolean {
  return (import.meta.env?.VITE_COMING_SOON as string | undefined)?.trim() === 'true';
}

/**
 * Whether Step 0 offers the marketing-consent tick (2026-09-23; server contract
 * clinic #218). Enabled by `VITE_STEP0_CONSENT_ENABLED=true` at build time —
 * the same per-environment pattern as `VITE_COMING_SOON`, and the same exact
 * string `'true'`: unset/empty/anything else keeps the box OFF, so a config
 * slip ships the capture step exactly as it is today rather than showing a
 * consent wording nobody has signed off. It is OFF EVERYWHERE until the CTO
 * signs the DRAFT wording `mc-2026-09-23-step0-d1` — see the header of
 * `marketing/step0ConsentWording.ts` for where that sign-off gets recorded.
 * Read as a function (not a module const) so it is `vi.stubEnv`-testable,
 * mirroring `comingSoonEnabled()` above.
 */
export function step0MarketingConsentEnabled(): boolean {
  return (import.meta.env?.VITE_STEP0_CONSENT_ENABLED as string | undefined)?.trim() === 'true';
}

/**
 * Thrown when a real (non-stub) Stripe Elements session is required but
 * `VITE_STRIPE_PUBLISHABLE_KEY` is missing or not a publishable key.
 * Never invents a fake `pk_` fallback — misconfiguration must fail loudly.
 */
export class MissingStripePublishableKeyError extends Error {
  constructor(message = 'VITE_STRIPE_PUBLISHABLE_KEY is not configured') {
    super(message);
    this.name = 'MissingStripePublishableKeyError';
  }
}

/**
 * Read and validate the Stripe publishable key for Elements.
 * Accepts `pk_test_*` / `pk_live_*` only. Call only when mounting real Elements
 * (`paymentSession.stub === false`); stub checkout never needs this key.
 */
export function requireStripePublishableKey(): string {
  const raw = (import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined)?.trim() ?? '';
  if (!raw) {
    throw new MissingStripePublishableKeyError();
  }
  if (!/^pk_(test|live)_/.test(raw)) {
    throw new MissingStripePublishableKeyError('VITE_STRIPE_PUBLISHABLE_KEY must be a Stripe publishable key');
  }
  return raw;
}

/**
 * GTM (A1) — the CSP-clean install. The web CONTAINER is served from our OWN
 * tagging server (default `sst.thegpservice.co.uk`) rather than
 * googletagmanager.com.
 *
 * DO NOT conclude that sst is the only origin the CSP needs — an earlier version of
 * this docblock said exactly that and it silently disabled all advertising
 * measurement. Serving the container first-party does NOT serve the TAG LIBRARIES
 * first-party: the container still tells the browser to fetch gtag.js from
 * www.googletagmanager.com, and the tags beacon to Google's own endpoints. See
 * nginx.conf and ./tracking/csp.spec.ts. ALSO NOTE: changing VITE_GTM_SERVER_URL to
 * a host that nginx.conf's script-src does not allow kills tracking silently — the
 * two must move together. Both values
 * are build-time `VITE_*` vars baked by the Dockerfile (same pattern as the
 * gateway URL / Stripe key). Read as functions (not module consts) so a push
 * happens at call time — testable via `vi.stubEnv`, mirroring the Stripe key.
 *
 * FAIL-CLOSED: an unset/blank container id means GTM never loads (no tracking),
 * so a misconfigured build ships WITHOUT analytics rather than pointing at the
 * wrong container.
 */
export function gtmContainerId(): string {
  // Clinics-in-Core: the Core-hosted image NEVER loads GTM. Every visitor there
  // is a signed-in GPS Core user reaching their own records, not acquisition
  // traffic, and the Core hosts carry no consent regime for this container.
  // Blanking the id here reuses the existing fail-closed path (loadGtm no-ops
  // on '', ConsentBanner renders nothing) rather than adding a second gate.
  if (isCoreHosted()) return '';
  return (import.meta.env?.VITE_GTM_CONTAINER_ID as string | undefined)?.trim() || '';
}

/** The first-party tagging-server origin the GTM loader + tags hit (no trailing slash). */
export function gtmServerUrl(): string {
  const raw = (import.meta.env?.VITE_GTM_SERVER_URL as string | undefined)?.trim() || 'https://sst.thegpservice.co.uk';
  return raw.replace(/\/+$/, '');
}

/* ── Clinics-in-Core (Clinics ADR-1, 2026-09-07): where this build is served, and by whom ── */

/**
 * BASENAME — the URL prefix this build is served under: '' at the origin root
 * (app.clinics — every build until 2026-09-07) or '/clinics' when GPS Core fronts
 * this SAME build under `<core-host>/clinics/*`.
 *
 * Derived from Vite's own `base` (`import.meta.env.BASE_URL`, set by `VITE_BASE`
 * at build time) rather than a second env var, so the asset URLs Vite emits, the
 * router's `basename` (main.tsx), every raw `<a href>` (ZoneExitLink and the few
 * hand-written anchors) and every absolute link the app mints (invite links,
 * plan-resume links) agree on ONE value that cannot drift. A react-router
 * `<Link>` handles the prefix itself; anything that is NOT a `<Link>` goes through
 * {@link withBasename}. Normalised: no trailing slash; '/' → ''.
 */
export const BASENAME: string = normaliseBasename(import.meta.env?.BASE_URL);

/** '/', './' or blank → ''; otherwise `/segment[/…]` with no trailing slash. */
export function normaliseBasename(base: string | undefined): string {
  const inner = (base ?? '')
    .trim()
    .replace(/^\.\//, '')
    .replace(/^\/+|\/+$/g, '');
  return inner ? `/${inner}` : '';
}

/** Prefix an app-root path ('/patients') with BASENAME. The identity when standalone. */
export function withBasename(path: string): string {
  return `${BASENAME}${path}`;
}

/**
 * HOST MODE — who is serving this build. `'core'` marks the image GPS Core serves
 * under `<core-host>/clinics/*` (`VITE_HOST_MODE=core`, baked like every other
 * VITE_* flag). Read as a function so it is `vi.stubEnv`-testable, exactly like
 * gtmContainerId(). Anything but the exact string 'core' is `'standalone'` — the
 * deliberate fail-safe direction: a config slip yields the app.clinics behaviour,
 * never a half-configured Core one.
 *
 * What 'core' changes, and nothing else — THE complete list:
 *   1. the public acquisition journey redirects to the standalone portal
 *      (auth/CoreHostGuard), and `/` goes to the signed-in person's portal or
 *      back to Core instead of to the journey (IndexRedirect);
 *   2. GTM never loads (gtmContainerId() above);
 *   3. a "Back to Consultations" bar is shown above every shell branch
 *      (ui/chrome CoreHostBar);
 *   4. the patient/staff session AND the pharmacy session live in sessionStorage
 *      (auth/session, pharmacy/session);
 *   5. /auth-core accepts the GPS Core sign-in handoff (auth/CoreHandoffPage).
 * Deliberately UNCHANGED in both modes: a mid-session 401 still routes to
 * `/auth?next=…` (Clinics credentials) — staff sign in there, and a patient who
 * arrived through Core can re-open Clinics from Core via the return bar to re-mint.
 * Revisit at P5/P6 if the rehearsal shows patients stranded on that page.
 */
export type HostMode = 'standalone' | 'core';
export function hostMode(): HostMode {
  return (import.meta.env?.VITE_HOST_MODE as string | undefined)?.trim() === 'core' ? 'core' : 'standalone';
}
export function isCoreHosted(): boolean {
  return hostMode() === 'core';
}

/**
 * The STANDALONE portal's origin (e.g. https://app.clinics.thegpservice.co.uk),
 * used only by the Core-hosted image: the public acquisition journey is not
 * served under Core, so `/:clinicSlug` and `/:clinicSlug/callback` redirect
 * there, and links the app mints into the journey point there. Blank when unset —
 * the guard then renders the journey in place rather than redirecting to
 * nowhere (visible and recoverable, never a dead end).
 *
 * VALIDATED, NOT TRUSTED. This value becomes the target of a
 * `window.location.replace()` and of `<a href>`s the app renders and mints into
 * SMS/email, so a mis-set build variable must degrade to "unset", never to a
 * `javascript:` URL or a relative path that dead-ends under the Core host. Only
 * an absolute `https:` origin is accepted (`http:` for localhost only); the
 * parsed `origin` is returned, so any path, query or fragment is dropped.
 */
export function publicPortalOrigin(): string {
  const raw = (import.meta.env?.VITE_PUBLIC_PORTAL_ORIGIN as string | undefined)?.trim() || '';
  if (!raw) return '';
  try {
    const url = new URL(raw);
    const isLocalhost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocalhost)) return '';
    return url.origin;
  } catch {
    return '';
  }
}

/**
 * Where the "Back to Consultations" bar returns to on the Core host — a
 * SAME-ORIGIN path, because the Core portal and this build share that host.
 * Defaults to Core's signed-in URL; `VITE_HOST_RETURN_PATH` overrides per portal.
 * Only a root-relative path in a conservative charset is accepted: anything else
 * could not be "back to the host" and would be an open redirect, so it falls back
 * rather than being trusted. The charset matters: browsers normalise a
 * backslash after the leading slash (`/\evil.example`) to `//evil.example` —
 * ANOTHER ORIGIN — so a "starts with one slash" check alone is not same-origin.
 */
// Path + query characters only (`?` cannot change the origin; a scheme, `//`, `\`,
// whitespace and angle brackets cannot appear).
const SAME_ORIGIN_PATH = /^\/(?![/\\])[A-Za-z0-9._~!$&'()*+,;=:@%/?-]*$/;
export function hostReturnPath(): string {
  const raw = (import.meta.env?.VITE_HOST_RETURN_PATH as string | undefined)?.trim() || '/dashboard';
  return SAME_ORIGIN_PATH.test(raw) ? raw : '/dashboard';
}

/**
 * The base every link INTO the public journey is minted against: this build's
 * own origin + BASENAME when standalone; the standalone portal when Core-hosted
 * (the journey is not served under Core, so a link minted inside Core must not
 * point at a Core host only to be bounced). Callers that pass an explicit base
 * (the specs) are unaffected.
 */
export function journeyBase(): string {
  const standalone = publicPortalOrigin();
  if (isCoreHosted() && standalone) return standalone;
  if (typeof window === 'undefined') return BASENAME;
  return `${window.location.origin}${BASENAME}`;
}
